import { effectiveFairnessDenominator } from "./participation.js";
import {
  WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
  normalizeWinterDoppelFairnessMode,
} from "./constants.js";

// Grobplanung: Starter + Warteliste + Kosten. Nach Finalize: Anteile fix.

export const WINTERDOPPEL_DETAIL_WINDOW_DAYS = 4;

export { normalizeWinterDoppelFairnessMode };

export function playingStarters(sessionPlayers = []) {
  return (sessionPlayers || []).filter(
    (sp) => sp && sp.role === "starter" && sp.rsvp !== "no",
  );
}

export function waitlistReserves(sessionPlayers = [], fairnessOpts = null) {
  const reserves = (sessionPlayers || []).filter(
    (sp) => sp && sp.role === "reserve" && sp.rsvp !== "no",
  );
  if (
    fairnessOpts &&
    (fairnessOpts.starterCounts || fairnessOpts.availabilityCounts)
  ) {
    return sortReservesByFairness(reserves, fairnessOpts);
  }
  return reserves
    .slice()
    .sort(
      (a, b) =>
        (Number(a.waitlist_rank) || 999) - (Number(b.waitlist_rank) || 999),
    );
}

/**
 * Vergleich für Starter- und Wartelisten-Priorität.
 * @returns {number} negativ = a vor b
 */
export function comparePlayersByFairness(
  idA,
  idB,
  {
    fairnessMode = WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
    starterCounts = new Map(),
    availabilityCounts = new Map(),
    participationCaps = new Map(),
  } = {},
) {
  const a = String(idA || "");
  const b = String(idB || "");
  const mode = normalizeWinterDoppelFairnessMode(fairnessMode);
  const qa = playerFairnessQuote(
    starterCounts,
    availabilityCounts,
    a,
    participationCaps,
  );
  const qb = playerFairnessQuote(
    starterCounts,
    availabilityCounts,
    b,
    participationCaps,
  );
  const ca = starterCounts.get(a) || 0;
  const cb = starterCounts.get(b) || 0;

  if (mode === "equal_load") {
    // Absolute Einsätze zuerst. Wer an dem Tag nicht kann, ist bereits
    // aus den Candidates gefiltert – „selten verfügbar“ bedeutet dann
    // automatisch weniger Termine, ohne die Sortierung auf Quote umzubiegen.
    if (ca !== cb) return ca - cb;
    if (qa !== qb) return qa - qb;
    return a.localeCompare(b, "de");
  }

  if (qa !== qb) return qa - qb;
  if (ca !== cb) return ca - cb;
  return a.localeCompare(b, "de");
}

export function sortReservesByFairness(
  reserves = [],
  {
    starterCounts = new Map(),
    availabilityCounts = new Map(),
    participationCaps = new Map(),
    fairnessMode = WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
  } = {},
) {
  const opts = {
    starterCounts,
    availabilityCounts,
    participationCaps,
    fairnessMode,
  };
  return (reserves || []).slice().sort((a, b) => {
    const idA = String(a?.player_id || "");
    const idB = String(b?.player_id || "");
    const cmp = comparePlayersByFairness(idA, idB, opts);
    if (cmp !== 0) return cmp;
    const ra = Number(a.waitlist_rank);
    const rb = Number(b.waitlist_rank);
    if (Number.isFinite(ra) && Number.isFinite(rb) && ra !== rb) {
      return ra - rb;
    }
    return idA.localeCompare(idB, "de");
  });
}

export function pickNextReserveForPromotion(
  sessionPlayers = [],
  fairnessOpts = null,
) {
  const ordered = waitlistReserves(sessionPlayers, fairnessOpts);
  return ordered[0] || null;
}

export function declinedStarters(sessionPlayers = []) {
  return (sessionPlayers || []).filter(
    (sp) => sp && sp.role === "starter" && sp.rsvp === "no",
  );
}

function __hasValidPairing(sp) {
  const court = Number(sp?.court_no);
  const slot = Number(sp?.pair_slot);
  return Number.isFinite(court) && court >= 1 && (slot === 0 || slot === 1);
}

/**
 * Freie Partner-Slots (alleinstehende Spieler zuerst, dann leere Slots).
 * @returns {{ court_no: number, pair_slot: number }|null}
 */
export function findWinterDoppelPairingHole(
  sessionPlayers = [],
  courtCount = 1,
) {
  const courts = Math.max(1, Number(courtCount) || 1);
  /** @type {Map<string, number>} */
  const occ = new Map();
  for (const sp of playingStarters(sessionPlayers)) {
    if (!__hasValidPairing(sp)) continue;
    const key = `${Number(sp.court_no)}|${Number(sp.pair_slot)}`;
    occ.set(key, (occ.get(key) || 0) + 1);
  }
  for (let court = 1; court <= courts; court += 1) {
    for (const slot of [0, 1]) {
      const n = occ.get(`${court}|${slot}`) || 0;
      if (n === 1) return { court_no: court, pair_slot: slot };
    }
  }
  for (let court = 1; court <= courts; court += 1) {
    let onCourt = 0;
    for (const slot of [0, 1]) {
      onCourt += occ.get(`${court}|${slot}`) || 0;
    }
    if (onCourt >= 4) continue;
    for (const slot of [0, 1]) {
      const n = occ.get(`${court}|${slot}`) || 0;
      if (n < 2) return { court_no: court, pair_slot: slot };
    }
  }
  return null;
}

/** Ungepaarte Starter in Löcher schieben (ohne bestehende Paarungen anzufassen). */
export function fillWinterDoppelPairingGaps(
  sessionPlayers = [],
  courtCount = 1,
) {
  const rows = sessionPlayers || [];
  for (const sp of playingStarters(rows)) {
    if (__hasValidPairing(sp)) continue;
    const hole = findWinterDoppelPairingHole(rows, courtCount);
    if (!hole) break;
    sp.court_no = hole.court_no;
    sp.pair_slot = hole.pair_slot;
  }
  return rows;
}

function __pairOccupancyKey(sp) {
  return `${Number(sp.court_no)}|${Number(sp.pair_slot)}`;
}

function __isSoloOnPairSlot(sp, starters) {
  if (!__hasValidPairing(sp)) return true;
  const key = __pairOccupancyKey(sp);
  let n = 0;
  for (const other of starters) {
    if (!__hasValidPairing(other)) continue;
    if (__pairOccupancyKey(other) === key) n += 1;
  }
  return n === 1;
}

function __courtPlayerCount(sp, starters) {
  const court = Number(sp?.court_no);
  if (!Number.isFinite(court) || court < 1) return 0;
  let n = 0;
  for (const other of starters) {
    if (Number(other?.court_no) === court) n += 1;
  }
  return n;
}

/** Wen demotieren: ungepaart → Solo-Slot → ungerade Platzgröße → Listenende. */
function __pickDemoteVictim(starters) {
  const list = starters || [];
  if (!list.length) return null;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (!__hasValidPairing(list[i])) return list[i];
  }
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (__isSoloOnPairSlot(list[i], list)) return list[i];
  }
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const n = __courtPlayerCount(list[i], list);
    if (n > 0 && n !== 2 && n !== 4) return list[i];
  }
  return list[list.length - 1];
}

/** Überzählige Starter ohne vollständiges Match → Warteliste (Mix-Kapazität). */
export function demoteExcessUnpairedStarters(
  sessionPlayers = [],
  courtCount = 1,
) {
  const rows = sessionPlayers || [];
  const cap = sessionPlayableCapacityForRows(rows, courtCount);
  while (playingStarters(rows).length > cap) {
    const victim = __pickDemoteVictim(playingStarters(rows));
    if (!victim) break;
    victim.role = "reserve";
    victim.court_no = null;
    victim.pair_slot = null;
    victim.substitute_for_player_id = null;
  }
  rerankWaitlist(rows);
  return rows;
}

/**
 * Mix erzwingen: Überhang auf Warteliste, Paarungslücken füllen.
 * @returns {boolean} true wenn Rollen/Courts geändert wurden
 */
export function normalizeWinterDoppelSessionCourtMix(
  sessionPlayers = [],
  courtCount = 1,
) {
  const rows = sessionPlayers || [];
  const snapshot = rows.map((sp) => ({
    role: sp.role,
    court_no: sp.court_no,
    pair_slot: sp.pair_slot,
  }));
  demoteExcessUnpairedStarters(rows, courtCount);
  fillWinterDoppelPairingGaps(rows, courtCount);
  rerankWaitlist(rows);
  return rows.some((sp, i) => {
    const before = snapshot[i];
    if (!before) return true;
    return (
      sp.role !== before.role ||
      sp.court_no !== before.court_no ||
      sp.pair_slot !== before.pair_slot
    );
  });
}

/** Absagender Starter mit noch nicht übertragenem Anteil (Nachzügler offen). */
export function declinedStartersWithTransferableCost(sessionPlayers = []) {
  return declinedStarters(sessionPlayers).filter(
    (sp) => Math.round((Number(sp.cost_share) || 0) * 100) / 100 > 0,
  );
}

/**
 * Nach Plan-Bestätigung: Anteil vom Absagenden an Nachzügler übertragen.
 * Kein Guthaben für den Absagenden (Pech – keine Rückerstattung).
 * @returns {number} übertragener Betrag in EUR
 */
export function transferLockedStarterCost(declined, recipient) {
  if (!declined || !recipient) return 0;
  const amount = Math.round((Number(declined.cost_share) || 0) * 100) / 100;
  if (amount <= 0) return 0;
  declined.cost_share = 0;
  recipient.cost_share =
    Math.round(((Number(recipient.cost_share) || 0) + amount) * 100) / 100;
  recipient.substitute_for_player_id = declined.player_id;
  return amount;
}

/**
 * Teilbetrag von einer fixierten Kostenzeile auf eine andere übertragen.
 * @param {{ bookCredit?: boolean }} [opts]
 *   bookCredit=true: Spender erhält Guthaben (ungeplanter Neuzugang).
 *   bookCredit=false: nur cost verschieben (z. B. Absage → Nachrücker).
 * @returns {number} übertragener Betrag in EUR
 */
export function transferLockedCostAmount(fromSp, toSp, amount, opts = {}) {
  if (!fromSp || !toSp) return 0;
  const bookCredit = Boolean(opts.bookCredit);
  const want = Math.round((Number(amount) || 0) * 100) / 100;
  const available = Math.round((Number(fromSp.cost_share) || 0) * 100) / 100;
  const take = Math.min(want, available);
  if (take <= 0) return 0;
  fromSp.cost_share = Math.round((available - take) * 100) / 100;
  if (bookCredit) {
    fromSp.credit_share =
      Math.round(((Number(fromSp.credit_share) || 0) + take) * 100) / 100;
  }
  toSp.cost_share =
    Math.round(((Number(toSp.cost_share) || 0) + take) * 100) / 100;
  return take;
}

/** Absolute Max-Kapazität (alle Plätze als Doppel) – z. B. für Abendkosten. */
export function sessionCapacity(courtCount) {
  return Math.max(1, Number(courtCount) || 1) * 4;
}

/**
 * Optimaler Mix aus Doppel (4) und Einzel (2) bei Unterbesetzung.
 * Maximiert: spielende Spieler → genutzte Plätze → Anzahl Doppel.
 */
export function suggestWinterDoppelCourtMix({ pool = 0, courtCount = 1 } = {}) {
  const courts = Math.max(1, Math.floor(Number(courtCount) || 1));
  const p = Math.max(0, Math.floor(Number(pool) || 0));
  let best = {
    doubles: 0,
    singles: 0,
    playing: 0,
    waitlist: p,
    unusedCourts: courts,
  };
  for (let d = 0; d <= courts; d += 1) {
    for (let s = 0; s <= courts - d; s += 1) {
      const playing = 4 * d + 2 * s;
      if (playing === 0 || playing > p) continue;
      const used = d + s;
      const bestUsed = best.doubles + best.singles;
      const better =
        playing > best.playing ||
        (playing === best.playing && used > bestUsed) ||
        (playing === best.playing && used === bestUsed && d > best.doubles);
      if (!better) continue;
      best = {
        doubles: d,
        singles: s,
        playing,
        waitlist: p - playing,
        unusedCourts: courts - used,
      };
    }
  }
  return best;
}

/** Spielbare Starter-Kapazität für eine gegebene Spieleranzahl. */
export function sessionPlayableCapacity(courtCount, pool) {
  return suggestWinterDoppelCourtMix({ pool, courtCount }).playing;
}

export function sessionAvailablePlayerCount(sessionPlayers = []) {
  return (
    playingStarters(sessionPlayers).length +
    waitlistReserves(sessionPlayers).length
  );
}

export function sessionPlayableCapacityForRows(sessionPlayers, courtCount) {
  return sessionPlayableCapacity(
    courtCount,
    sessionAvailablePlayerCount(sessionPlayers),
  );
}

/** Starter = Mix-Kapazität, Rest als Warteliste. */
export function pickStartersAndWaitlist(candidates = [], courtCount) {
  const list = Array.isArray(candidates) ? candidates : [];
  const cap = sessionPlayableCapacity(courtCount, list.length);
  return {
    starters: list.slice(0, cap),
    reserves: list.slice(cap),
  };
}

/**
 * Quote = Geplant ÷ Verfügbar (nur Starter, nicht Warteliste).
 * Niedrigere Quote = höhere Priorität für nächsten Starter-Slot.
 */
export function playerFairnessQuote(
  starterCounts = new Map(),
  availabilityCounts = new Map(),
  playerId,
  participationCaps = new Map(),
) {
  const pid = String(playerId || "");
  const avail = availabilityCounts.get(pid) || 0;
  const cap = participationCaps.get(pid);
  const denom = effectiveFairnessDenominator(avail, cap == null ? null : cap);
  if (denom <= 0) return Number.POSITIVE_INFINITY;
  return (starterCounts.get(pid) || 0) / denom;
}

/**
 * Starter nach Fairness-Modus (Quote oder gleiche Belastung), Rest als Warteliste.
 */
export function pickStartersAndWaitlistByQuote(
  candidates = [],
  courtCount,
  starterCounts = new Map(),
  availabilityCounts = new Map(),
  rng = Math.random,
  participationCaps = new Map(),
  fairnessMode = WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
) {
  const list = Array.isArray(candidates) ? candidates : [];
  const cap = sessionPlayableCapacity(courtCount, list.length);
  const opts = {
    starterCounts,
    availabilityCounts,
    participationCaps,
    fairnessMode,
  };
  const sorted = list.slice().sort((a, b) => {
    const idA = String(a?.id || "");
    const idB = String(b?.id || "");
    const cmp = comparePlayersByFairness(idA, idB, opts);
    if (cmp !== 0) return cmp;
    return rng() - 0.5;
  });
  return {
    starters: sorted.slice(0, cap),
    reserves: sorted.slice(cap),
  };
}

/** Alias – gleiche Signatur inkl. fairnessMode. */
export function pickStartersAndWaitlistByFairness(
  candidates,
  courtCount,
  starterCounts,
  availabilityCounts,
  rng,
  participationCaps,
  fairnessMode,
) {
  return pickStartersAndWaitlistByQuote(
    candidates,
    courtCount,
    starterCounts,
    availabilityCounts,
    rng,
    participationCaps,
    fairnessMode,
  );
}

/**
 * Soft: zieht höchstens einen Wunschpartner nach, wenn ein Starter
 * noch keinen Wunschpartner unter den Startern hat.
 */
export function applyForcedBondsToStarterSelection({
  starters = [],
  reserves = [],
  forcedPairs = [],
  courtCount,
} = {}) {
  const cap = sessionPlayableCapacity(
    courtCount,
    (starters || []).length + (reserves || []).length,
  );
  const starterList = (starters || []).slice();
  const reserveList = (reserves || []).slice();
  const starterIds = new Set(starterList.map((p) => String(p?.id || "")));

  /** @type {Map<string, Set<string>>} */
  const prefs = new Map();
  for (const raw of forcedPairs || []) {
    const a = String(raw?.[0] || "");
    const b = String(raw?.[1] || "");
    if (!a || !b || a === b) continue;
    if (!prefs.has(a)) prefs.set(a, new Set());
    if (!prefs.has(b)) prefs.set(b, new Set());
    prefs.get(a).add(b);
    prefs.get(b).add(a);
  }
  if (prefs.size === 0) {
    return { starters: starterList, reserves: reserveList };
  }

  const hasPreferredStarterPartner = (id) => {
    const want = prefs.get(id);
    if (!want) return false;
    for (const p of want) {
      if (starterIds.has(p)) return true;
    }
    return false;
  };

  const protectedIds = new Set();
  for (const [id, want] of prefs) {
    if (!starterIds.has(id)) continue;
    for (const p of want) {
      if (starterIds.has(p)) {
        protectedIds.add(id);
        protectedIds.add(p);
      }
    }
  }

  const needing = starterList
    .map((p) => String(p?.id || ""))
    .filter((id) => id && prefs.has(id) && !hasPreferredStarterPartner(id));

  for (const starterId of needing) {
    if (hasPreferredStarterPartner(starterId)) continue;
    const want = [...(prefs.get(starterId) || [])].sort((x, y) =>
      x.localeCompare(y),
    );
    let pickIdx = -1;
    let pickId = "";
    for (const pid of want) {
      const idx = reserveList.findIndex((p) => String(p?.id || "") === pid);
      if (idx < 0) continue;
      pickIdx = idx;
      pickId = pid;
      break;
    }
    if (pickIdx < 0) continue;

    const [missing] = reserveList.splice(pickIdx, 1);
    if (starterList.length < cap) {
      starterList.push(missing);
      starterIds.add(pickId);
      protectedIds.add(starterId);
      protectedIds.add(pickId);
      continue;
    }

    let demoteIdx = -1;
    for (let i = starterList.length - 1; i >= 0; i -= 1) {
      const id = String(starterList[i]?.id || "");
      if (id === starterId) continue;
      if (protectedIds.has(id)) continue;
      demoteIdx = i;
      break;
    }
    if (demoteIdx < 0) {
      reserveList.push(missing);
      continue;
    }
    const [demoted] = starterList.splice(demoteIdx, 1);
    starterIds.delete(String(demoted?.id || ""));
    reserveList.unshift(demoted);
    starterList.push(missing);
    starterIds.add(pickId);
    protectedIds.add(starterId);
    protectedIds.add(pickId);
  }

  return { starters: starterList, reserves: reserveList };
}

/**
 * Zieht Gruppenmitglieder (2+) nach, wenn mindestens einer bereits Starter ist.
 * Nur für feste Spielgruppen (Training), nicht für optionale Partnerwünsche.
 * @param {{ starters: object[], reserves: object[], forcedGroups?: string[][], courtCount: number }}
 */
export function applyForcedGroupsToStarterSelection({
  starters = [],
  reserves = [],
  forcedGroups = [],
  courtCount,
} = {}) {
  const cap = sessionPlayableCapacity(
    courtCount,
    (starters || []).length + (reserves || []).length,
  );
  const starterList = (starters || []).slice();
  const reserveList = (reserves || []).slice();
  const starterIds = new Set(starterList.map((p) => String(p?.id || "")));
  const bonded = new Set();
  const groups = (forcedGroups || [])
    .map((g) => [
      ...new Set((Array.isArray(g) ? g : []).map(String).filter(Boolean)),
    ])
    .filter((g) => g.length >= 2);

  for (const g of groups) {
    for (const id of g) bonded.add(id);
  }

  for (const group of groups) {
    const inStarters = group.filter((id) => starterIds.has(id));
    if (inStarters.length === 0) continue;
    if (inStarters.length === group.length) continue;

    for (const missingId of group) {
      if (starterIds.has(missingId)) continue;
      const missingIdx = reserveList.findIndex(
        (p) => String(p?.id || "") === missingId,
      );
      if (missingIdx < 0) continue;
      const [missing] = reserveList.splice(missingIdx, 1);

      if (starterList.length < cap) {
        starterList.push(missing);
        starterIds.add(missingId);
        continue;
      }

      let demoteIdx = -1;
      for (let i = starterList.length - 1; i >= 0; i -= 1) {
        const id = String(starterList[i]?.id || "");
        if (group.includes(id)) continue;
        if (bonded.has(id)) continue;
        demoteIdx = i;
        break;
      }
      if (demoteIdx < 0) {
        reserveList.push(missing);
        continue;
      }
      const [demoted] = starterList.splice(demoteIdx, 1);
      starterIds.delete(String(demoted?.id || ""));
      reserveList.unshift(demoted);
      starterList.push(missing);
      starterIds.add(missingId);
    }
  }

  return { starters: starterList, reserves: reserveList };
}

/**
 * Training: ganze Gruppen auf Plätze legen (nicht Spieler mischen).
 * Bei zu wenigen Plätzen: Gruppen mit weniger bisherigen Einsätzen zuerst.
 */
export function pickTrainingGroupsForSession({
  groupSchedules = [],
  sessionDate,
  courtCount,
  starterCounts = new Map(),
  availablePlayerIds = null,
} = {}) {
  const date = String(sessionDate || "").slice(0, 10);
  const capCourts = Math.max(1, Number(courtCount) || 1);
  const avail =
    availablePlayerIds instanceof Set
      ? availablePlayerIds
      : new Set((availablePlayerIds || []).map(String));

  const active = (groupSchedules || []).filter((gs) => {
    if (!gs?.dates?.has?.(date)) return false;
    const members = (gs.playerIds || []).filter(
      (id) => !avail.size || avail.has(String(id)),
    );
    return members.length >= 2;
  });

  const scored = active
    .map((gs) => {
      const members = (gs.playerIds || [])
        .map(String)
        .filter((id) => !avail.size || avail.has(id));
      const starts = members.reduce(
        (s, id) => s + (Number(starterCounts.get(id)) || 0),
        0,
      );
      const avg = members.length ? starts / members.length : 0;
      return { gs, members, avg };
    })
    .sort((a, b) => {
      if (a.avg !== b.avg) return a.avg - b.avg;
      return String(a.gs.group?.id || "").localeCompare(
        String(b.gs.group?.id || ""),
      );
    });

  const chosen = scored.slice(0, capCourts);
  const starterIds = new Set();
  for (const c of chosen) {
    for (const id of c.members) starterIds.add(id);
  }
  // Training: keine Warteliste – Gruppen ohne Platz spielen an dem Termin nicht.
  return { starterIds: [...starterIds], reserveIds: [] };
}

/** @deprecated Nutze pickStartersAndWaitlistByQuote */
export function pickStartersAndWaitlistBalanced(
  candidates = [],
  courtCount,
  assignmentCounts = new Map(),
  rng = Math.random,
) {
  const availability = new Map();
  for (const p of candidates || []) {
    if (p?.id) availability.set(String(p.id), 1);
  }
  return pickStartersAndWaitlistByQuote(
    candidates,
    courtCount,
    assignmentCounts,
    availability,
    rng,
  );
}

export function sessionDurationHours(startTime, endTime) {
  const start = __hmToMinutes(startTime);
  const end = __hmToMinutes(endTime);
  if (start == null || end == null || end <= start) return 0;
  return Math.round(((end - start) / 60) * 100) / 100;
}

/**
 * Saison-Abo: EUR/h/Platz × Stunden × Plätze (einmal für die ganze Saison).
 * Bsp. 570 × 2 h × 2 Plätze = 2.280 € Gesamtkosten.
 */
export function seasonAboTotal(hourlyRate, courtCount, opts = {}) {
  const hours = sessionDurationHours(opts.startTime, opts.endTime);
  const hourly = Math.max(0, Number(hourlyRate) || 0);
  const n = Math.max(1, Number(courtCount) || 1);
  if (hourly <= 0 || hours <= 0) return 0;
  return Math.round(hourly * hours * n * 100) / 100;
}

/**
 * Abendkosten je Termin.
 * Abo-Modus: Saison-Abo ÷ Terminanzahl.
 * Fallback: Platzpreis × Plätze je Termin.
 */
export function eveningTotal(courtFee, courtCount, opts = {}) {
  const n = Math.max(1, Number(courtCount) || 1);
  const sessions = Math.max(0, Math.floor(Number(opts.sessionCount) || 0));
  const abo = seasonAboTotal(opts.hourlyRate, courtCount, opts);
  if (abo > 0 && sessions > 0) {
    return Math.round((abo / sessions) * 100) / 100;
  }
  const fee = Math.max(0, Number(courtFee) || 0);
  return Math.round(fee * n * 100) / 100;
}

/** Saison-Gesamt: bei Abo der Abo-Betrag, sonst Abend × Termine. */
export function seasonTotal(evening, sessionCount, opts = {}) {
  const abo = seasonAboTotal(opts.hourlyRate, opts.courtCount ?? 1, opts);
  if (abo > 0) return abo;
  const n = Math.max(0, Math.floor(Number(sessionCount) || 0));
  return Math.round((Number(evening) || 0) * n * 100) / 100;
}

function __hmToMinutes(value) {
  const m = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Restcent-gerecht auf n Anteile (EUR, 2 Nachkommastellen). */
export function splitCostShares(total, count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n <= 0) return [];
  const cents = Math.round((Number(total) || 0) * 100);
  const base = Math.floor(cents / n);
  const rem = cents - base * n;
  return Array.from({ length: n }, (_, i) => (base + (i < rem ? 1 : 0)) / 100);
}

export function shuffleCopy(arr, rng = Math.random) {
  const a = Array.isArray(arr) ? arr.slice() : [];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a;
}

export function isDetailPlanningWindow(sessionDate, today = new Date()) {
  const d = String(sessionDate || "").slice(0, 10);
  const t =
    today instanceof Date
      ? today.toISOString().slice(0, 10)
      : String(today || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    return false;
  }
  const diff =
    (Date.parse(`${d}T12:00:00`) - Date.parse(`${t}T12:00:00`)) / 86400000;
  return diff >= 0 && diff <= WINTERDOPPEL_DETAIL_WINDOW_DAYS;
}

export function rerankWaitlist(sessionPlayers = []) {
  for (const sp of sessionPlayers || []) {
    if (sp.role === "reserve") sp.waitlist_rank = null;
    else if (sp.role === "starter") sp.waitlist_rank = null;
  }
  return sessionPlayers;
}

export function isSeasonAboMode(costOpts = {}, courtCount = 1) {
  return seasonAboTotal(costOpts.hourlyRate, courtCount, costOpts) > 0;
}

function __costShareSortKey(sp) {
  return `${String(sp?.session_id || "")}\t${String(sp?.player_id || sp?.id || "")}`;
}

/** Saison-Abo: Gesamtbetrag gleichmäßig auf alle Grobplanungs-Starter-Einsätze. */
export function assignSeasonAboCostShares(
  sessionPlayers = [],
  courtFee,
  courtCount,
  costOpts = {},
) {
  const rows = Array.isArray(sessionPlayers) ? sessionPlayers : [];
  const abo = seasonAboTotal(costOpts.hourlyRate, courtCount, costOpts);
  if (abo <= 0) return false;

  const playing = playingStarters(rows)
    .slice()
    .sort((a, b) =>
      __costShareSortKey(a).localeCompare(__costShareSortKey(b), "de"),
    );
  const shares = splitCostShares(abo, playing.length);
  const byRef = new Map(playing.map((sp, i) => [sp, shares[i]]));
  for (const sp of rows) {
    sp.cost_share = byRef.get(sp) ?? 0;
  }
  return true;
}

/** Pro Termin: Abendkosten auf die Starter dieses Termins (Platzpreis-Fallback). */
export function assignDraftCostShares(
  sessionPlayers = [],
  courtFee,
  courtCount,
  costOpts = {},
) {
  const playing = playingStarters(sessionPlayers);
  const total =
    costOpts.eveningCost != null
      ? Number(costOpts.eveningCost) || 0
      : eveningTotal(courtFee, courtCount, costOpts);
  const shares = splitCostShares(total, playing.length);
  const byRef = new Map(playing.map((sp, i) => [sp, shares[i]]));
  for (const sp of sessionPlayers || []) {
    if (byRef.has(sp)) sp.cost_share = byRef.get(sp);
    else sp.cost_share = 0;
  }
  return sessionPlayers;
}

/**
 * Kosten auf Basis der Grobplanung: Abo → je Starter-Einsatz, sonst je Termin.
 */
export function assignAllDraftCostShares(
  allRows,
  courtFee,
  courtCount,
  costOpts = {},
) {
  const rows = Array.isArray(allRows) ? allRows : [];
  if (isSeasonAboMode(costOpts, courtCount)) {
    assignSeasonAboCostShares(rows, courtFee, courtCount, costOpts);
    return rows;
  }
  const bySession = new Map();
  for (const sp of rows) {
    const sid = String(sp?.session_id || "");
    if (!sid) continue;
    const list = bySession.get(sid) || [];
    list.push(sp);
    bySession.set(sid, list);
  }
  for (const group of bySession.values()) {
    assignDraftCostShares(group, courtFee, courtCount, costOpts);
  }
  return rows;
}

/**
 * Absage eines Starters: Nachrücken von der Warteliste.
 * Draft: Kosten neu teilen. Final: Anteil an Nachzügler ohne Guthaben für Absagenden (Pech).
 */
export function applyStarterDecline(
  sessionPlayers = [],
  declinedKey,
  {
    locked = false,
    courtFee = 0,
    courtCount = 2,
    fairnessContext = null,
    /** Training: keine Doppel/Einzel-Mix-Demotion (Gruppe bleibt Starter). */
    skipCourtMix = false,
    ...costOpts
  } = {},
) {
  const key = String(declinedKey || "");
  const next = (sessionPlayers || []).map((sp) => ({ ...sp }));
  const declined = next.find(
    (sp) => String(sp.id) === key || String(sp.player_id) === key,
  );
  if (!declined || declined.role !== "starter") return next;

  const vacatedCourt = Number(declined.court_no);
  const vacatedSlot = Number(declined.pair_slot);
  const hadSlot =
    Number.isFinite(vacatedCourt) &&
    vacatedCourt >= 1 &&
    (vacatedSlot === 0 || vacatedSlot === 1);

  declined.rsvp = "no";
  declined.court_no = null;
  declined.pair_slot = null;

  if (skipCourtMix) {
    if (!locked) assignDraftCostShares(next, courtFee, courtCount, costOpts);
    return next;
  }

  const cap = sessionPlayableCapacityForRows(next, courtCount);
  if (playingStarters(next).length < cap) {
    const promo = pickNextReserveForPromotion(next, fairnessContext);
    if (promo) {
      promo.role = "starter";
      promo.waitlist_rank = null;
      promo.substitute_for_player_id = declined.player_id;
      if (locked) {
        promo.rsvp = "yes";
        transferLockedStarterCost(declined, promo);
      }
      if (hadSlot) {
        promo.court_no = vacatedCourt;
        promo.pair_slot = vacatedSlot;
      }
    }
  }

  demoteExcessUnpairedStarters(next, courtCount);
  fillWinterDoppelPairingGaps(next, courtCount);
  rerankWaitlist(next);
  if (!locked) assignDraftCostShares(next, courtFee, courtCount, costOpts);
  return next;
}

function findSubstituteForPlayer(sessionPlayers, playerId) {
  const pid = String(playerId || "");
  if (!pid) return null;
  return (
    (sessionPlayers || []).find(
      (row) =>
        row &&
        row.role === "starter" &&
        row.rsvp !== "no" &&
        String(row.substitute_for_player_id || "") === pid &&
        String(row.player_id || "") !== pid,
    ) || null
  );
}

function reclaimStarterFromSubstitute(next, sp, { locked = false } = {}) {
  const sub = findSubstituteForPlayer(next, sp.player_id);
  if (!sub) return false;
  if (sp.court_no == null && sub.court_no != null) {
    sp.court_no = sub.court_no;
    sp.pair_slot = sub.pair_slot;
  }
  if (locked) {
    transferLockedCostAmount(sub, sp, Number(sub.cost_share) || 0, {
      bookCredit: false,
    });
  }
  sp.role = "starter";
  sp.waitlist_rank = null;
  sp.substitute_for_player_id = null;
  sub.role = "reserve";
  sub.court_no = null;
  sub.pair_slot = null;
  sub.substitute_for_player_id = null;
  return true;
}

/**
 * Absage rückgängig: Starter wieder aktiv.
 * War jemand von der Warteliste nachgerückt, geht der zurück auf die Warteliste.
 * Sonst bei voller Kapazität → selbst auf die Warteliste.
 */
export function applyStarterUndecline(
  sessionPlayers = [],
  playerKey,
  { locked = false, courtCount = 2, rsvp = null, skipCourtMix = false } = {},
) {
  const key = String(playerKey || "");
  const next = (sessionPlayers || []).map((sp) => ({ ...sp }));
  const sp = next.find(
    (row) => String(row.id) === key || String(row.player_id) === key,
  );
  if (!sp || sp.rsvp !== "no") return next;
  if (sp.role !== "starter" && sp.role !== "reserve") return next;

  const nextRsvp =
    rsvp === "yes" || rsvp === "pending" ? rsvp : locked ? "yes" : "pending";
  sp.rsvp = nextRsvp;

  if (skipCourtMix) {
    if (sp.role === "reserve") sp.role = "starter";
    sp.waitlist_rank = null;
    return next;
  }

  if (reclaimStarterFromSubstitute(next, sp, { locked })) {
    rerankWaitlist(next);
    fillWinterDoppelPairingGaps(next, courtCount);
    demoteExcessUnpairedStarters(next, courtCount);
    return next;
  }

  if (sp.role !== "starter") {
    rerankWaitlist(next);
    return next;
  }

  const poolIfActive =
    playingStarters(next).length + waitlistReserves(next).length;
  const cap = sessionPlayableCapacity(courtCount, poolIfActive);
  if (playingStarters(next).length > cap) {
    sp.role = "reserve";
    sp.court_no = null;
    sp.pair_slot = null;
    rerankWaitlist(next);
    return next;
  }

  sp.role = "starter";
  while (
    playingStarters(next).length <
    sessionPlayableCapacityForRows(next, courtCount)
  ) {
    const promo = pickNextReserveForPromotion(next);
    if (!promo) break;
    promo.role = "starter";
    promo.waitlist_rank = null;
    if (locked) promo.rsvp = "yes";
  }
  fillWinterDoppelPairingGaps(next, courtCount);
  demoteExcessUnpairedStarters(next, courtCount);
  rerankWaitlist(next);
  return next;
}

export function applyReservePromote(
  sessionPlayers = [],
  reserveKey,
  {
    locked = false,
    courtFee = 0,
    courtCount = 2,
    replacesPlayerId = null,
    ...costOpts
  } = {},
) {
  const key = String(reserveKey || "");
  const next = (sessionPlayers || []).map((sp) => ({ ...sp }));
  const promo = next.find(
    (sp) => String(sp.id) === key || String(sp.player_id) === key,
  );
  if (!promo || promo.role !== "reserve") return next;
  promo.role = "starter";
  promo.waitlist_rank = null;
  if (locked) {
    const replaceId = String(replacesPlayerId || "");
    const donor = replaceId
      ? next.find(
          (sp) =>
            sp.role === "starter" &&
            sp.rsvp === "no" &&
            String(sp.player_id) === replaceId,
        )
      : declinedStartersWithTransferableCost(next)[0];
    if (donor) transferLockedStarterCost(donor, promo);
    else promo.cost_share = Number(promo.cost_share) || 0;
  }
  rerankWaitlist(next);
  if (!locked) assignDraftCostShares(next, courtFee, courtCount, costOpts);
  return next;
}

/**
 * Nachzügler eintragen (Serien-Spieler, der bisher nicht am Termin war).
 */
export function applyLateJoiner(
  sessionPlayers = [],
  playerId,
  replacesPlayerId,
  { locked = false, courtFee = 0, courtCount = 2, ...costOpts } = {},
) {
  const pid = String(playerId || "");
  const replaceId = String(replacesPlayerId || "");
  if (!pid || !replaceId) return sessionPlayers;

  const next = (sessionPlayers || []).map((sp) => ({ ...sp }));
  const existing = next.find((sp) => String(sp.player_id) === pid);
  const donor = next.find(
    (sp) =>
      sp.role === "starter" &&
      sp.rsvp === "no" &&
      String(sp.player_id) === replaceId,
  );
  if (!donor) return next;

  let recipient = existing;
  if (existing) {
    // Abgesagter Starter erneut einplanen (z. B. sich selbst als „Nachzügler“)
    if (existing.role === "starter" && existing.rsvp === "no") {
      existing.rsvp = locked ? "yes" : "pending";
      if (
        locked &&
        donor &&
        String(donor.player_id) !== pid &&
        Math.round((Number(existing.cost_share) || 0) * 100) / 100 <= 0
      ) {
        transferLockedStarterCost(donor, existing);
      }
      return next;
    }
    if (existing.role !== "reserve") return next;
    existing.role = "starter";
    existing.waitlist_rank = null;
    existing.rsvp =
      existing.rsvp === "no" ? (locked ? "yes" : "pending") : existing.rsvp;
  } else {
    recipient = {
      player_id: pid,
      role: "starter",
      rsvp: locked ? "yes" : "pending",
      court_no: null,
      pair_slot: null,
      waitlist_rank: null,
      cost_share: 0,
      credit_share: 0,
      substitute_for_player_id: null,
    };
    next.push(recipient);
  }

  if (locked) transferLockedStarterCost(donor, recipient);
  rerankWaitlist(next);
  if (!locked) assignDraftCostShares(next, courtFee, courtCount, costOpts);
  return next;
}
